export interface NxPickFilesOptions {
  /** The `accept` filter, e.g. `'image/*'` or `['.csv', '.xlsx']`. */
  accept?: string | readonly string[];
  multiple?: boolean;
}

/**
 * Open the browser's file chooser without a visible `<input>` and resolve
 * with the chosen files. A dismissed chooser resolves with `[]` through the
 * input's `cancel` event (all current browsers). The transient input is
 * removed on either outcome, so repeated opens do not leak nodes.
 *
 * Call it from a user gesture (a click handler); browsers block file
 * choosers opened any other way.
 */
export function pickFiles(options: NxPickFilesOptions = {}): Promise<File[]> {
  if (typeof document === 'undefined') return Promise.resolve([]);
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    if (options.accept) {
      input.accept = typeof options.accept === 'string' ? options.accept : options.accept.join(',');
    }
    input.multiple = !!options.multiple;
    input.style.display = 'none';

    let settled = false;
    const finish = (files: File[]) => {
      if (settled) return;
      settled = true;
      input.remove();
      resolve(files);
    };
    input.addEventListener('change', () => finish(Array.from(input.files ?? [])));
    input.addEventListener('cancel', () => finish([]));

    document.body.appendChild(input);
    input.click();
  });
}
